/**
 * THE CAPTION — words laid OVER a picture or a clip, anchored to one of the media's nine spots.
 *
 * ## Why this exists next to `mediaOverlay`
 *
 * The `image` section shipped wordless on purpose, and the note at the top of its schema said so: anything
 * that needed words already had a section (image-with-text for a side-by-side, hero for an overlay). The
 * founder's answer on 19/09/2026 is the one that matters — a seller stacking ten exported visuals does not
 * want a DIFFERENT section the moment one of them needs a line of text on it, and a hero is a page opener,
 * not a caption. Same for `video`: its heading sits ABOVE the clip, centred, and that is a title for the
 * band — not a word placed on the frame.
 *
 * So the words come to the media, and they come with a POSITION: top-left … bottom-right, the nine-spot
 * grid `hero` already speaks (`desktopContentPosition`), spelled with the same nine values so a seller who
 * has met one control has met both — and so the builder's dictionaries already answer `opt.top-left` and
 * its eight siblings in seven languages.
 *
 * ## The contract
 *
 * **An empty caption paints NOTHING.** No layer, no rule, no byte. These fields land on sections that are
 * live on real storefronts today, so an untouched section renders exactly what it rendered yesterday —
 * the same contract `mediaOverlay` and every per-device setting in `device.ts` obey.
 *
 * **The layer never eats a click.** The image frame can be an `<a>` (the whole picture is a link) and the
 * video poster IS a `<button>` — an overlay that swallows the pointer is the same defect as a button that
 * does nothing, and this project has already paid for it once (`sticky-cta`, 16/09). So the layer is
 * `pointer-events:none` by construction, and only the section that knows there is nothing underneath to
 * click hands the text box back its pointer.
 *
 * **Position, size, width and inset are per-device rules, never inline styles.** An inline declaration
 * outranks any `@media` rule whatever the source order, so a mobile override written inline would be dead
 * on arrival — see `device.ts` for the full version of that lesson.
 */
import { animationFields, blinkTextCss, readBlinkSpeed, readTextAnimation, shinyTextCss, } from '../../blocks/_shared/textAnimation.js';
import { sanitizeRichtextHtml } from '../../sanitize/sanitizeRichtextHtml.js';
import { cssColor, num, str } from './settingValue.js';
import { perDeviceCss } from './device.js';
const POSITIONS = [
    'top-left',
    'top-center',
    'top-right',
    'middle-left',
    'middle-center',
    'middle-right',
    'bottom-left',
    'bottom-center',
    'bottom-right',
];
const BACKDROPS = ['none', 'shadow', 'panel'];
/** The nine options, ready to spread into a select. `opt.<value>` already answers in all seven languages. */
const POSITION_OPTIONS = [
    { value: 'top-left', label: 'Top left' },
    { value: 'top-center', label: 'Top center' },
    { value: 'top-right', label: 'Top right' },
    { value: 'middle-left', label: 'Middle left' },
    { value: 'middle-center', label: 'Middle center' },
    { value: 'middle-right', label: 'Middle right' },
    { value: 'bottom-left', label: 'Bottom left' },
    { value: 'bottom-center', label: 'Bottom center' },
    { value: 'bottom-right', label: 'Bottom right' },
];
/** The inspector heading these fields file under — one spelling, so one translation serves every section. */
export const CAPTION_GROUP = 'Text over the media';
/** The caption's animation fields are shown only once there is a caption to animate. */
const WHEN_CAPTION = { field: 'mediaCaptionText', empty: false };
/**
 * The caption's own fields — text, placement, scale, colour, backdrop.
 *
 * `defaultPosition` is a parameter and not a constant because the two sections that carry this engine have
 * genuinely different geometry: an `image` is a flat picture and the middle is where a seller writes on it,
 * while a `video` poster has a round play button sitting in that exact spot. Defaulting the clip's caption
 * to the bottom-left keeps the two controls out of each other's way on a section nobody has configured yet.
 */
export function mediaCaptionFields(group = CAPTION_GROUP, defaultPosition = 'middle-center') {
    const onlyPanel = { field: 'mediaCaptionBackdrop', equals: ['panel'] };
    return [
        {
            id: 'mediaCaptionText',
            type: 'richtext',
            label: 'Text over the media',
            group,
            info: 'Leave it empty and nothing is painted — the media stays exactly as it is. Bold and italic work here; the size below sets the scale of the whole block.',
        },
        {
            id: 'mediaCaptionPosition',
            type: 'select',
            label: 'Text position',
            group,
            default: defaultPosition,
            aiConfigurable: true,
            info: 'Where the words sit ON the media — the nine anchor points of the frame.',
            options: POSITION_OPTIONS.map((o) => ({ ...o })),
        },
        {
            id: 'mediaCaptionSize',
            type: 'range',
            label: 'Text size',
            group,
            min: 12,
            max: 72,
            step: 2,
            unit: 'px',
            default: 28,
        },
        {
            id: 'mediaCaptionWidth',
            type: 'range',
            label: 'Text width',
            group,
            min: 20,
            max: 100,
            step: 5,
            unit: '%',
            default: 60,
            info: 'How much of the picture the words may span before they wrap. A narrow column is what keeps a caption reading as a caption.',
        },
        {
            id: 'mediaCaptionInset',
            type: 'range',
            label: 'Distance from the edges',
            group,
            min: 0,
            max: 80,
            step: 4,
            unit: 'px',
            default: 24,
        },
        {
            id: 'mediaCaptionColor',
            type: 'color',
            label: 'Text color',
            group,
            default: { ref: 'custom', value: '#ffffff' },
        },
        {
            id: 'mediaCaptionBackdrop',
            type: 'select',
            label: 'Behind the text',
            group,
            default: 'shadow',
            info: 'White letters vanish on a pale photo. A shadow keeps them readable without covering anything; a panel is the stronger answer when the picture is busy.',
            options: [
                { value: 'none', label: 'Nothing' },
                { value: 'shadow', label: 'Soft shadow' },
                { value: 'panel', label: 'Tinted panel' },
            ],
        },
        {
            id: 'mediaCaptionBackdropColor',
            type: 'color',
            label: 'Panel color',
            group,
            default: { ref: 'custom', value: '#000000' },
            visibleWhen: onlyPanel,
        },
        {
            id: 'mediaCaptionBackdropOpacity',
            type: 'range',
            label: 'Panel strength',
            group,
            min: 0,
            max: 100,
            step: 5,
            unit: '%',
            default: 45,
            visibleWhen: onlyPanel,
        },
        // The words' entrance — Split / Shiny / Blur / Typing, lines and direction included — filed under
        // the caption's own heading and hidden until there is a caption. Same ids as the heading block, so
        // `readTextAnimation` reads them and the seller's dictionary already names every one of them.
        ...animationFields({ group, when: WHEN_CAPTION }),
    ];
}
/**
 * THE PHONE'S OWN CAPTION (the four settings that genuinely travel badly).
 *
 * Not the text — one caption, two readers; a seller who wants two different sentences wants two sections.
 * Not the colour or the backdrop either: those follow the picture, and the picture is the same one.
 *
 * What DOES change between a 1 900px banner and a 390px panel is where the words fit and how big they are:
 * `bottom-right` on a wide photo is a signature, and on a phone it is a line squeezed against the corner;
 * 44px is a headline on a desktop and four words per line on a phone. So those four move, and each of them
 * means "same as desktop" while it is untouched.
 */
export function mobileMediaCaptionFields(group = 'Mobile') {
    return [
        {
            id: 'mobileMediaCaptionPosition',
            type: 'select',
            label: 'Text position',
            group,
            default: 'inherit',
            aiConfigurable: true,
            info: 'A spot that reads well across a wide photo often lands in the corner of a tall one.',
            options: [
                { value: 'inherit', label: 'Same as desktop' },
                ...POSITION_OPTIONS.map((o) => ({ ...o })),
            ],
        },
        {
            id: 'mobileMediaCaptionSize',
            type: 'range',
            label: 'Text size',
            group,
            min: 12,
            max: 72,
            step: 2,
            unit: 'px',
            inheritFrom: 'mediaCaptionSize',
        },
        {
            id: 'mobileMediaCaptionWidth',
            type: 'range',
            label: 'Text width',
            group,
            min: 20,
            max: 100,
            step: 5,
            unit: '%',
            inheritFrom: 'mediaCaptionWidth',
        },
        {
            id: 'mobileMediaCaptionInset',
            type: 'range',
            label: 'Distance from the edges',
            group,
            min: 0,
            max: 80,
            step: 4,
            unit: 'px',
            inheritFrom: 'mediaCaptionInset',
        },
    ];
}
/**
 * A caption value → the HTML to paint, or `''` when there is nothing to say.
 *
 * Sanitized every time (the live XSS barrier — a stored document is JSON and can have been written by
 * anything), and NEVER throws: a mis-shaped value degrades to `''` rather than blanking the whole page,
 * because SSR has no error boundary around a section's render.
 */
export function captionHtml(v) {
    if (v == null)
        return '';
    if (typeof v === 'string')
        return hasWords(v) ? sanitizeRichtextHtml(v) : '';
    if (typeof v !== 'object')
        return '';
    const rt = v;
    if (rt.format !== 'html' || typeof rt.html !== 'string')
        return '';
    const clean = sanitizeRichtextHtml(rt.html);
    return hasWords(clean) ? clean : '';
}
/**
 * Does this HTML actually say something?
 *
 * The rich-text control never hands back an empty string — it hands back `<p></p>`, or `<p><br></p>` the
 * moment the seller typed and deleted a word. Treating that as a caption would paint an invisible panel
 * over every picture on the storefront, and a seller who cleared the field would have no way to tell.
 */
function hasWords(html) {
    if (/<(img|iframe)\b/i.test(html))
        return true;
    return (html
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;| /gi, ' ')
        .trim() !== '');
}
/** Read the desktop caption off the untyped document — `null` when the seller wrote nothing. */
export function readMediaCaption(s) {
    const html = captionHtml(s.mediaCaptionText);
    if (html === '')
        return null;
    const raw = str(s.mediaCaptionPosition, 'middle-center');
    const backdrop = str(s.mediaCaptionBackdrop, 'shadow');
    return {
        html,
        position: POSITIONS.includes(raw) ? raw : 'middle-center',
        size: clamp(num(s.mediaCaptionSize, 28), 12, 72),
        width: clamp(num(s.mediaCaptionWidth, 60), 20, 100),
        inset: clamp(num(s.mediaCaptionInset, 24), 0, 80),
        color: cssColor(s.mediaCaptionColor, '#ffffff'),
        backdrop: BACKDROPS.includes(backdrop) ? backdrop : 'shadow',
        backdropColor: cssColor(s.mediaCaptionBackdropColor, '#000000'),
        backdropOpacity: clamp(num(s.mediaCaptionBackdropOpacity, 45), 0, 100),
        textAnim: readTextAnimation(s),
        shinySpeed: clamp(num(s.shinySpeed, 5), 1, 20),
        blinkSpeed: readBlinkSpeed(s),
    };
}
/** The phone's four overrides, each falling back to the desktop's own value. */
export function readMobileMediaCaption(s, desktop) {
    const raw = str(s.mobileMediaCaptionPosition, 'inherit');
    const position = raw === 'inherit' || raw === '' || !POSITIONS.includes(raw)
        ? desktop.position
        : raw;
    return {
        position,
        size: clamp(num(s.mobileMediaCaptionSize, desktop.size), 12, 72),
        width: clamp(num(s.mobileMediaCaptionWidth, desktop.width), 20, 100),
        inset: clamp(num(s.mobileMediaCaptionInset, desktop.inset), 0, 80),
    };
}
function clamp(v, min, max) {
    return Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : min;
}
/** `color` at `pct`% alpha — `color-mix` so a theme token survives the blend (same trick as the overlay). */
function alpha(color, pct) {
    const p = Math.round(Math.min(100, Math.max(0, pct)) * 10) / 10;
    if (p <= 0)
        return 'transparent';
    return `color-mix(in srgb, ${color} ${p}%, transparent)`;
}
/** top|middle|bottom → the flex `align-items` that pins the block to that edge. */
function alignItems(p) {
    if (p.startsWith('top-'))
        return 'flex-start';
    if (p.startsWith('bottom-'))
        return 'flex-end';
    return 'center';
}
/** left|center|right → the flex `justify-content` that pins the block to that side. */
function justifyContent(p) {
    if (p.endsWith('-left'))
        return 'flex-start';
    if (p.endsWith('-right'))
        return 'flex-end';
    return 'center';
}
/**
 * `text-align`, in LOGICAL keywords.
 *
 * `start`/`end` and not `left`/`right`: an Arabic storefront reads the same document, and a caption pinned
 * to the reading edge is what "left" means to the seller who chose it — the RTL lesson this project already
 * learned once on `scrollLeft`.
 */
function textAlign(p) {
    if (p.endsWith('-left'))
        return 'start';
    if (p.endsWith('-right'))
        return 'end';
    return 'center';
}
/**
 * Every rule the caption needs, scoped to the two classes the section owns.
 *
 * `layer` is the absolutely-positioned box that fills the media frame; `box` is the text column inside it.
 * Everything that can differ per device is emitted through `perDeviceCss`, which writes the phone's answer
 * in BOTH queries — `@media` for the storefront and `@container aqx-page` for the builder's device preview,
 * which resizes a container and not the window. A section that emits only the first has a mobile design
 * the seller cannot see while they are setting it.
 */
export function mediaCaptionCss(layer, box, d, m) {
    const panel = d.backdrop === 'panel' && d.backdropOpacity > 0
        ? `background:${alpha(d.backdropColor, d.backdropOpacity)};` +
            `padding:0.6em 0.9em;border-radius:var(--aqx-theme-radius-media, 6px)`
        : '';
    // A shadow and not a panel is the default because it costs the picture nothing: two soft drops, one
    // tight enough to give the letters an edge, one wide enough to lift them off a pale sky.
    const shadow = d.backdrop === 'shadow'
        ? 'text-shadow:0 1px 3px rgba(0,0,0,0.45), 0 2px 14px rgba(0,0,0,0.35)'
        : '';
    return [
        // `pointer-events:none` is NOT negotiable here — see the file header. The section hands the box back
        // its pointer only where it knows nothing underneath is clickable.
        `${layer}{position:absolute;inset:0;display:flex;z-index:2;pointer-events:none}`,
        perDeviceCss(layer, `align-items:${alignItems(d.position)};justify-content:${justifyContent(d.position)};padding:${d.inset}px`, `align-items:${alignItems(m.position)};justify-content:${justifyContent(m.position)};padding:${m.inset}px`),
        // `line-height` is written, never inherited: the image frame sets `line-height:0` on itself so a
        // cropped <img> leaves no descender gap, and an inherited 0 would stack every line of the caption on
        // top of the last one. (Found the hard way; the rule below is the whole fix.)
        `${box}{max-inline-size:100%;line-height:1.3;color:${d.color}` +
            (shadow ? `;${shadow}` : '') +
            (panel ? `;${panel}` : '') +
            '}',
        perDeviceCss(box, `inline-size:${d.width}%;text-align:${textAlign(d.position)};font-size:${d.size}px`, `inline-size:${m.width}%;text-align:${textAlign(m.position)};font-size:${m.size}px`),
        // The rich-text editor's own block wrappers. Left alone, a `<p>` brings the browser's 1em margins and
        // an `<h2>` its 32px — the caption would sit off its anchor by a margin nobody set.
        `${box} p{margin:0}`,
        `${box} p+p,${box} h1+p,${box} h2+p,${box} h3+p{margin-block-start:0.4em}`,
        `${box} h1,${box} h2,${box} h3{margin:0;font-size:1.25em;line-height:1.15;` +
            'font-family:var(--aqx-theme-heading-font, var(--aqx-theme-font))}',
        `${box} ul,${box} ol{margin:0.3em 0;padding-inline-start:1.2em}`,
        // A link inside the caption keeps the caption's colour — the theme's link blue on a photo is the
        // contrast defect `comparison-table` already paid for.
        `${box} a{color:inherit}`,
        // "Shiny" is pure CSS, so it is the caption's stylesheet that carries it (the other effects are the
        // shared engine's, hooked through `data-aqx-textanim` on the box).
        d.textAnim.type === 'shiny'
            ? shinyTextCss(box, `aqx-cap-shine-${box.replace(/[^a-zA-Z0-9_-]/g, '')}`, d.color, d.shinySpeed)
            : '',
        // "Blink" — the same arrangement: a continuous CSS effect the caption's stylesheet carries itself.
        d.textAnim.type === 'blink'
            ? blinkTextCss(box, `aqx-cap-blink-${box.replace(/[^a-zA-Z0-9_-]/g, '')}`, d.blinkSpeed)
            : '',
    ].join('');
}
//# sourceMappingURL=mediaCaption.js.map